import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import ExitIntentPopup from '../components/ExitIntentPopup';
import { Button } from '../components/ui/button';
import { 
  Phone, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Download, 
  TrendingUp,
  MoreHorizontal,
  Trash2,
  Check,
  Play,
  Search,
  Calendar
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country codes mapping for flag display
const countryPrefixes = [
  { prefix: '+33', code: 'fr', name: 'France' },
  { prefix: '+32', code: 'be', name: 'Belgique' },
  { prefix: '+41', code: 'ch', name: 'Suisse' },
  { prefix: '+1', code: 'us', name: 'USA/Canada' },
  { prefix: '+352', code: 'lu', name: 'Luxembourg' },
  { prefix: '+377', code: 'mc', name: 'Monaco' },
  { prefix: '+49', code: 'de', name: 'Allemagne' },
  { prefix: '+34', code: 'es', name: 'Espagne' },
  { prefix: '+39', code: 'it', name: 'Italie' },
  { prefix: '+44', code: 'gb', name: 'UK' },
  { prefix: '+351', code: 'pt', name: 'Portugal' },
  { prefix: '+31', code: 'nl', name: 'Pays-Bas' },
  { prefix: '+212', code: 'ma', name: 'Maroc' },
  { prefix: '+213', code: 'dz', name: 'Algérie' },
  { prefix: '+216', code: 'tn', name: 'Tunisie' },
  { prefix: '+221', code: 'sn', name: 'Sénégal' },
  { prefix: '+225', code: 'ci', name: 'Côte d\'Ivoire' }
];

// Get flag image URL from phone number
const getFlagFromPhone = (phone) => {
  if (!phone) return null;
  // Sort by prefix length descending to match longer prefixes first (e.g., +352 before +3)
  const sortedPrefixes = [...countryPrefixes].sort((a, b) => b.prefix.length - a.prefix.length);
  for (const country of sortedPrefixes) {
    if (phone.startsWith(country.prefix)) {
      return `https://flagcdn.com/24x18/${country.code}.png`;
    }
  }
  return null; // No match
};

// Format phone number for display
const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  // Add spaces for readability
  const cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+33')) {
    // French format: +33 6 12 34 56 78
    return cleaned.replace(/(\+33)(\d)(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5 $6');
  }
  if (cleaned.startsWith('+32')) {
    // Belgian format: +32 4 71 23 45 67
    return cleaned.replace(/(\+32)(\d)(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5 $6');
  }
  // Default: add space after country code
  return cleaned.replace(/(\+\d{1,3})(\d+)/, '$1 $2');
};

const Dashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const [callbacks, setCallbacks] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, last_7_days: 0 });
  const [loading, setLoading] = useState(true);
  const [showDemo, setShowDemo] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [callbacksRes, statsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/callbacks`),
        axios.get(`${API}/callbacks/stats`),
        axios.get(`${API}/settings`)
      ]);
      setCallbacks(callbacksRes.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (callbackId, newStatus) => {
    try {
      await axios.patch(`${API}/callbacks/${callbackId}/status?status=${newStatus}`);
      setCallbacks(prev => 
        prev.map(cb => cb.id === callbackId ? { ...cb, status: newStatus } : cb)
      );
      setStats(prev => ({
        ...prev,
        pending: newStatus === 'completed' ? prev.pending - 1 : prev.pending + 1,
        completed: newStatus === 'completed' ? prev.completed + 1 : prev.completed - 1
      }));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (callbackId) => {
    try {
      await axios.delete(`${API}/callbacks/${callbackId}`);
      setCallbacks(prev => prev.filter(cb => cb.id !== callbackId));
      setStats(prev => ({ ...prev, total: prev.total - 1 }));
      toast.success('Request deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/callbacks/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `callbacks_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export completed');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { icon: Clock, className: isDark ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-amber-100 text-amber-700 border-amber-200' },
      completed: { icon: CheckCircle, className: isDark ? 'bg-[#3272F1]/20 text-[#3272F1] border-[#3272F1]/30' : 'bg-blue-100 text-blue-700 border-blue-200' },
      cancelled: { icon: XCircle, className: isDark ? 'bg-gray-500/20 text-gray-400 border-gray-500/30' : 'bg-gray-100 text-gray-600 border-gray-200' }
    };
    const { icon: Icon, className } = variants[status] || variants.pending;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${className}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const statCards = [
    { key: 'total', label: t('totalRequests'), value: stats.total, icon: Phone, color: 'from-gray-500 to-gray-600', textColor: isDark ? 'text-white' : 'text-gray-900' },
    { key: 'pending', label: t('pending'), value: stats.pending, icon: Clock, color: 'from-amber-500 to-orange-500', textColor: 'text-amber-500' },
    { key: 'completed', label: t('completed'), value: stats.completed, icon: CheckCircle, color: 'from-[#3272F1] to-blue-600', textColor: 'text-[#3272F1]' },
    { key: 'recent', label: t('last7Days'), value: stats.last_7_days, icon: TrendingUp, color: 'from-cyan-500 to-blue-500', textColor: 'text-cyan-500' },
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`} style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Navbar />
      
      {/* Background effects - only for dark mode */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3272F1]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-3xl"></div>
        </div>
      )}
      
      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className={`text-3xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('callbackRequests')}
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              {user?.company_name}
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => setShowDemo(true)}
              variant="outline"
              className={`gap-2 ${isDark ? 'bg-[#3272F1]/10 border-[#3272F1]/30 text-[#3272F1] hover:bg-[#3272F1]/20' : 'bg-[#3272F1]/5 border-[#3272F1]/20 text-[#3272F1] hover:bg-[#3272F1]/10'}`}
              data-testid="test-widget-btn"
            >
              <Play className="w-4 h-4" />
              {language === 'fr' ? 'Tester le widget' : 'Test widget'}
            </Button>
            <Button 
              onClick={handleExport}
              variant="outline"
              className={`gap-2 ${isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              data-testid="export-btn"
            >
              <Download className="w-4 h-4" />
              {t('exportCsv')}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map(({ key, label, value, icon: Icon, color, textColor }) => (
            <div 
              key={key} 
              className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-sm'} rounded-2xl p-6 border`}
              data-testid={`stat-${key}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm mb-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{label}</p>
                  <p className={`text-3xl font-bold ${textColor}`}>{value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-sm'} rounded-2xl border overflow-hidden`}>
          <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'} flex items-center justify-between`}>
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('callbackRequests')}</h2>
            <div className={`flex items-center gap-2 ${isDark ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg px-3 py-2`}>
              <Search className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input 
                type="text" 
                placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                className={`bg-transparent border-0 text-sm ${isDark ? 'text-gray-300 placeholder-gray-500' : 'text-gray-700 placeholder-gray-400'} focus:outline-none w-40`}
              />
            </div>
          </div>
          
          {loading ? (
            <div className={`p-12 text-center ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              <div className="w-8 h-8 border-2 border-[#3272F1] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              Loading...
            </div>
          ) : callbacks.length === 0 ? (
            <div className="p-12 text-center">
              <div className={`w-16 h-16 ${isDark ? 'bg-gray-800' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <Phone className={`w-8 h-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('noCallbacks')}</h3>
              <p className={isDark ? 'text-gray-500' : 'text-gray-600'}>{t('noCallbacksDesc')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <th className={`text-left px-6 py-4 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t('phone')}</th>
                    <th className={`text-left px-6 py-4 text-xs font-medium uppercase tracking-wider hidden lg:table-cell ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t('page')}</th>
                    <th className={`text-left px-6 py-4 text-xs font-medium uppercase tracking-wider hidden md:table-cell ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{language === 'fr' ? 'Créneau' : 'Time slot'}</th>
                    <th className={`text-left px-6 py-4 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t('status')}</th>
                    <th className={`text-left px-6 py-4 text-xs font-medium uppercase tracking-wider hidden md:table-cell ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t('date')}</th>
                    <th className="w-[50px]"></th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-200'}`}>
                  {callbacks.map((callback) => (
                    <tr key={callback.id} className={`${isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'} transition-colors`} data-testid={`callback-row-${callback.id}`}>
                      <td className={`px-6 py-4 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <div className="flex items-center gap-2">
                          {getFlagFromPhone(callback.phone) && (
                            <img 
                              src={getFlagFromPhone(callback.phone)} 
                              alt="" 
                              className="w-6 h-4 rounded-sm object-cover"
                            />
                          )}
                          <span>{formatPhoneDisplay(callback.phone)}</span>
                        </div>
                      </td>
                      <td className={`px-6 py-4 max-w-[200px] truncate hidden lg:table-cell ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {callback.page_url}
                      </td>
                      <td className={`px-6 py-4 hidden md:table-cell`}>
                        {callback.selected_slot_formatted ? (
                          <div className="flex items-center gap-2">
                            <Calendar className={`w-4 h-4 ${isDark ? 'text-[#3272F1]' : 'text-[#3272F1]'}`} />
                            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {callback.selected_slot_formatted}
                            </span>
                          </div>
                        ) : (
                          <span className={`text-sm ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(callback.status)}</td>
                      <td className={`px-6 py-4 hidden md:table-cell ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                        {formatDate(callback.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className={isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'} data-testid={`actions-${callback.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className={isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}>
                            {callback.status !== 'completed' && (
                              <DropdownMenuItem 
                                onClick={() => handleStatusChange(callback.id, 'completed')}
                                className={isDark ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-100'}
                                data-testid={`complete-${callback.id}`}
                              >
                                <Check className="w-4 h-4 mr-2" />
                                {t('markCompleted')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => handleDelete(callback.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-gray-800"
                              data-testid={`delete-${callback.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t('delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Demo Popup */}
      {showDemo && settings && (
        <ExitIntentPopup
          userId={user?.id}
          settings={settings}
          language={language}
          onClose={() => setShowDemo(false)}
          forceShow={true}
        />
      )}
    </div>
  );
};

export default Dashboard;
